import React, { useState, useEffect, useCallback } from 'react';
import ToolLayout from '../components/ToolLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Folder, Download, Upload, FileText, X, Circle, CheckCircle2, Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import {
  getMergedSections,
  getDefaultAuditState,
  exportAuditToBinderSections,
  STATUS_PHYSICAL,
  STATUS_DIGITAL,
  STATUS_MISSING,
} from '../lib/documentAudit';
import { buildBinderExport, BINDER_EXPORT_EXTENSION } from '../lib/binderExportFormat';
import { toast } from 'sonner';

const STORAGE_KEY_AUDIT = 'document_audit_data';
const STORAGE_KEY_UPLOADS = 'document_uploads';
const STORAGE_KEY_CUSTOM = 'document_custom_items';
const STORAGE_KEY_CUSTOM_SECTIONS = 'document_custom_sections';
const STORAGE_KEY_ORDER = 'document_section_order';
const STORAGE_KEY_ITEM_ORDER = 'document_item_order';
const STORAGE_KEY_HIDDEN = 'document_hidden_items';
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

/** Inline row for adding a custom category (section). */
const AddCustomSectionRow = ({ onAdd }) => {
  const [value, setValue] = useState('');
  const handleSubmit = (e) => {
    e.preventDefault();
    if (value.trim()) {
      onAdd(value);
      setValue('');
    }
  };
  return (
    <form onSubmit={handleSubmit} className="flex items-center gap-2">
      <Plus className="h-4 w-4 text-muted-foreground shrink-0" />
      <Input
        type="text"
        placeholder="Add a category..."
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="h-9 text-sm flex-1"
      />
      <Button type="submit" variant="outline" size="sm" className="h-9 px-4 text-xs" disabled={!value.trim()}>
        Add category
      </Button>
    </form>
  );
};

/** Inline row for adding a custom document item to a section. */
const AddCustomItemRow = ({ onAdd }) => {
  const [value, setValue] = useState('');
  const handleSubmit = (e) => {
    e.preventDefault();
    if (value.trim()) {
      onAdd(value);
      setValue('');
    }
  };
  return (
    <form onSubmit={handleSubmit} className="flex items-center gap-2 pt-1">
      <Plus className="h-4 w-4 text-muted-foreground shrink-0" />
      <Input
        type="text"
        placeholder="Add a document..."
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="h-8 text-sm flex-1"
      />
      <Button type="submit" variant="ghost" size="sm" className="h-8 px-3 text-xs" disabled={!value.trim()}>
        Add
      </Button>
    </form>
  );
};

/**
 * Important Documents: Document Audit (7 sections).
 * Export from here is one file; combine it with other tool exports in Build your binder for one cohesive binder (cover, TOC, sections).
 */
const DocumentsTool = () => {
  const [auditState, setAuditState] = useState(getDefaultAuditState);
  const [uploads, setUploads] = useState({});
  const [customItems, setCustomItems] = useState({});
  const [customSections, setCustomSections] = useState([]);
  const [sectionOrder, setSectionOrder] = useState([]);
  const [itemOrder, setItemOrder] = useState({});
  const [hiddenItems, setHiddenItems] = useState({});

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_AUDIT);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          setAuditState((prev) => ({ ...getDefaultAuditState(), ...prev, ...parsed }));
        }
      }
    } catch (_) {
      // ignore
    }
    try {
      const rawUploads = localStorage.getItem(STORAGE_KEY_UPLOADS);
      if (rawUploads) {
        const parsed = JSON.parse(rawUploads);
        if (parsed && typeof parsed === 'object') setUploads(parsed);
      }
    } catch (_) {
      // ignore
    }
    try {
      const rawCustom = localStorage.getItem(STORAGE_KEY_CUSTOM);
      if (rawCustom) {
        const parsed = JSON.parse(rawCustom);
        if (parsed && typeof parsed === 'object') setCustomItems(parsed);
      }
    } catch (_) {
      // ignore
    }
    try {
      const rawSections = localStorage.getItem(STORAGE_KEY_CUSTOM_SECTIONS);
      if (rawSections) {
        const parsed = JSON.parse(rawSections);
        if (Array.isArray(parsed)) setCustomSections(parsed);
      }
    } catch (_) {
      // ignore
    }
    try {
      const rawOrder = localStorage.getItem(STORAGE_KEY_ORDER);
      if (rawOrder) {
        const parsed = JSON.parse(rawOrder);
        if (Array.isArray(parsed)) setSectionOrder(parsed);
      }
    } catch (_) {
      // ignore
    }
    try {
      const rawItemOrder = localStorage.getItem(STORAGE_KEY_ITEM_ORDER);
      if (rawItemOrder) {
        const parsed = JSON.parse(rawItemOrder);
        if (parsed && typeof parsed === 'object') setItemOrder(parsed);
      }
    } catch (_) {
      // ignore
    }
    try {
      const rawHidden = localStorage.getItem(STORAGE_KEY_HIDDEN);
      if (rawHidden) {
        const parsed = JSON.parse(rawHidden);
        if (parsed && typeof parsed === 'object') setHiddenItems(parsed);
      }
    } catch (_) {
      // ignore
    }
  }, []);

  const persistAudit = useCallback((nextState) => {
    setAuditState(nextState);
    try {
      localStorage.setItem(STORAGE_KEY_AUDIT, JSON.stringify(nextState));
    } catch (_) {}
  }, []);

  const persistUploads = useCallback((nextState) => {
    setUploads(nextState);
    try {
      localStorage.setItem(STORAGE_KEY_UPLOADS, JSON.stringify(nextState));
    } catch (e) {
      toast.error('Could not save upload — localStorage may be full.');
    }
  }, []);

  const persistCustomItems = useCallback((nextState) => {
    setCustomItems(nextState);
    try {
      localStorage.setItem(STORAGE_KEY_CUSTOM, JSON.stringify(nextState));
    } catch (_) {}
  }, []);

  const persistCustomSections = useCallback((nextState) => {
    setCustomSections(nextState);
    try {
      localStorage.setItem(STORAGE_KEY_CUSTOM_SECTIONS, JSON.stringify(nextState));
    } catch (_) {}
  }, []);

  const persistSectionOrder = useCallback((nextOrder) => {
    setSectionOrder(nextOrder);
    try {
      localStorage.setItem(STORAGE_KEY_ORDER, JSON.stringify(nextOrder));
    } catch (_) {}
  }, []);

  const persistItemOrder = useCallback((nextState) => {
    setItemOrder(nextState);
    try {
      localStorage.setItem(STORAGE_KEY_ITEM_ORDER, JSON.stringify(nextState));
    } catch (_) {}
  }, []);

  const persistHiddenItems = useCallback((nextState) => {
    setHiddenItems(nextState);
    try {
      localStorage.setItem(STORAGE_KEY_HIDDEN, JSON.stringify(nextState));
    } catch (_) {}
  }, []);

  const handleRemoveItem = (sectionId, itemId) => {
    // Determine if this is a custom item or a built-in item
    const isCustom = (customItems[sectionId] || []).some((i) => i.id === itemId);
    if (isCustom) {
      const next = { ...customItems };
      if (next[sectionId]) {
        next[sectionId] = next[sectionId].filter((i) => i.id !== itemId);
        if (next[sectionId].length === 0) delete next[sectionId];
      }
      persistCustomItems(next);
    } else {
      // Hide built-in item
      persistHiddenItems({
        ...hiddenItems,
        [sectionId]: [...(hiddenItems[sectionId] || []), itemId],
      });
    }
    // Clean up audit state, uploads, and item order
    const nextAudit = { ...auditState };
    if (nextAudit[sectionId]) {
      delete nextAudit[sectionId][itemId];
      persistAudit(nextAudit);
    }
    const nextUploads = { ...uploads };
    if (nextUploads[sectionId]?.[itemId]) {
      delete nextUploads[sectionId][itemId];
      if (Object.keys(nextUploads[sectionId]).length === 0) delete nextUploads[sectionId];
      persistUploads(nextUploads);
    }
    if (itemOrder[sectionId]) {
      const nextOrder = { ...itemOrder, [sectionId]: itemOrder[sectionId].filter((id) => id !== itemId) };
      if (nextOrder[sectionId].length === 0) delete nextOrder[sectionId];
      persistItemOrder(nextOrder);
    }
  };

  const handleMoveItem = (sectionId, itemId, direction) => {
    // Get current effective item list for this section
    const currentMerged = getMergedSections(customItems, customSections, sectionOrder, itemOrder, hiddenItems);
    const section = currentMerged.find((s) => s.id === sectionId);
    if (!section) return;
    const ids = section.items.map((i) => i.id);
    const idx = ids.indexOf(itemId);
    if (idx < 0) return;
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= ids.length) return;
    const newIds = [...ids];
    [newIds[idx], newIds[targetIdx]] = [newIds[targetIdx], newIds[idx]];
    persistItemOrder({ ...itemOrder, [sectionId]: newIds });
  };

  const handleAddCustomSection = (label) => {
    const trimmed = label.trim();
    if (!trimmed) return;
    const id = `section-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const nextSections = [...customSections, { id, label: trimmed }];
    persistCustomSections(nextSections);
    // Initialize audit state for the new section
    persistAudit({ ...auditState, [id]: {} });
    // Add to end of order (compute current effective order first)
    const currentMerged = getMergedSections(customItems, customSections, sectionOrder, itemOrder, hiddenItems);
    const currentIds = currentMerged.map((s) => s.id);
    persistSectionOrder([...currentIds, id]);
  };

  const handleRemoveCustomSection = (sectionId) => {
    persistCustomSections(customSections.filter((s) => s.id !== sectionId));
    // Clean up custom items, audit state, uploads, item order for this section
    const nextCustom = { ...customItems };
    delete nextCustom[sectionId];
    persistCustomItems(nextCustom);
    const nextAudit = { ...auditState };
    delete nextAudit[sectionId];
    persistAudit(nextAudit);
    const nextUploads = { ...uploads };
    delete nextUploads[sectionId];
    persistUploads(nextUploads);
    const nextItemOrder = { ...itemOrder };
    delete nextItemOrder[sectionId];
    persistItemOrder(nextItemOrder);
    // Remove from section order
    persistSectionOrder(sectionOrder.filter((id) => id !== sectionId));
  };

  const handleMoveSection = (sectionId, direction) => {
    const currentMerged = getMergedSections(customItems, customSections, sectionOrder, itemOrder, hiddenItems);
    const ids = currentMerged.map((s) => s.id);
    const idx = ids.indexOf(sectionId);
    if (idx < 0) return;
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= ids.length) return;
    const newIds = [...ids];
    [newIds[idx], newIds[targetIdx]] = [newIds[targetIdx], newIds[idx]];
    persistSectionOrder(newIds);
  };

  const handleAddCustomItem = (sectionId, label) => {
    const trimmed = label.trim();
    if (!trimmed) return;
    const id = `custom-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const next = {
      ...customItems,
      [sectionId]: [...(customItems[sectionId] || []), { id, label: trimmed }],
    };
    persistCustomItems(next);
    // Initialize status to missing
    persistAudit({
      ...auditState,
      [sectionId]: {
        ...auditState[sectionId],
        [id]: STATUS_MISSING,
      },
    });
  };

  const setItemStatus = (sectionId, itemId, status) => {
    persistAudit({
      ...auditState,
      [sectionId]: {
        ...auditState[sectionId],
        [itemId]: status,
      },
    });
  };

  const handleFileUpload = (sectionId, itemId, file) => {
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`File too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum is 2MB.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      const next = {
        ...uploads,
        [sectionId]: {
          ...uploads[sectionId],
          [itemId]: {
            fileName: file.name,
            mimeType: file.type,
            data: base64,
            uploadedAt: new Date().toISOString(),
          },
        },
      };
      persistUploads(next);
      // Auto-set status to digital
      if (auditState[sectionId]?.[itemId] !== STATUS_DIGITAL) {
        persistAudit({
          ...auditState,
          [sectionId]: {
            ...auditState[sectionId],
            [itemId]: STATUS_DIGITAL,
          },
        });
      }
      toast.success(`Uploaded ${file.name}`);
    };
    reader.onerror = () => toast.error('Failed to read file.');
    reader.readAsDataURL(file);
  };

  const handleRemoveUpload = (sectionId, itemId) => {
    const next = { ...uploads };
    if (next[sectionId]) {
      delete next[sectionId][itemId];
      if (Object.keys(next[sectionId]).length === 0) delete next[sectionId];
    }
    persistUploads(next);
    toast.success('Upload removed.');
  };

  const mergedSections = getMergedSections(customItems, customSections, sectionOrder, itemOrder, hiddenItems);

  const handleExport = () => {
    const auditSections = exportAuditToBinderSections(auditState, uploads, customItems, customSections, sectionOrder, itemOrder, hiddenItems);
    const payload = buildBinderExport('documents', 'Important Documents', auditSections);
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Important_Documents_${new Date().toISOString().split('T')[0]}${BINDER_EXPORT_EXTENSION}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Export downloaded. Upload this file in Build your binder.');
  };

  return (
    <ToolLayout
      title="Important Documents"
      description="Document audit checklist. Export here, then combine with other tools in Build your binder for one binder with table of contents."
      icon={Folder}
      guide={{
        intro: 'Audit your important documents and upload digital copies.',
        steps: [
          'Mark each document as Physical, Digital, or Missing.',
          'Upload scans or photos of documents (auto-sets to Digital).',
          'Add custom documents or entire categories for items we didn\'t think of.',
          'Reorder categories and items with the up/down arrows.',
          'Remove items you don\'t need.',
          'Export for your binder.',
        ],
      }}
    >
      <div className="max-w-3xl mx-auto space-y-6">
        <p className="text-muted-foreground">
          Mark documents as Physical, Digital, or Missing
          and get a prioritized task list. Pet ID cards (photo, microchip, vet) are now in the{' '}
          <a href="/emergency-plan" className="text-primary hover:underline font-medium">Emergency Plan</a>{' '}
          Pets tab. Export once, then add this file in Build your binder with your other exports.
        </p>

        <div className="space-y-6">
          {mergedSections.map((section, sectionIndex) => {
            const isCustomSection = !!section.isCustomSection;
            return (
              <Card key={section.id}>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-lg flex-1">{section.label}</CardTitle>
                    <div className="flex items-center gap-0.5">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        disabled={sectionIndex === 0}
                        onClick={() => handleMoveSection(section.id, -1)}
                      >
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        disabled={sectionIndex === mergedSections.length - 1}
                        onClick={() => handleMoveSection(section.id, 1)}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                      {isCustomSection && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={() => handleRemoveCustomSection(section.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <CardDescription>
                    Mark each document or item as you have it (physical or digital) or missing.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {section.items.map((item, itemIndex) => {
                    const upload = uploads[section.id]?.[item.id];
                    const status = auditState[section.id]?.[item.id] ?? STATUS_MISSING;
                    const isAccounted = status === STATUS_PHYSICAL || status === STATUS_DIGITAL;
                    return (
                      <div
                        key={item.id}
                        className="flex flex-col gap-2 py-2 border-b border-border/50 last:border-0"
                      >
                        <div className="flex flex-wrap items-center gap-3">
                          {isAccounted ? (
                            <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                          ) : (
                            <Circle className="h-5 w-5 text-muted-foreground/40 shrink-0" />
                          )}
                          <div className="flex flex-col shrink-0">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-4 w-4 p-0"
                              disabled={itemIndex === 0}
                              onClick={() => handleMoveItem(section.id, item.id, -1)}
                            >
                              <ChevronUp className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-4 w-4 p-0"
                              disabled={itemIndex === section.items.length - 1}
                              onClick={() => handleMoveItem(section.id, item.id, 1)}
                            >
                              <ChevronDown className="h-3 w-3" />
                            </Button>
                          </div>
                          <Label className="flex-1 min-w-[200px] text-sm font-medium">{item.label}</Label>
                          <Select
                            value={auditState[section.id]?.[item.id] ?? STATUS_MISSING}
                            onValueChange={(value) => setItemStatus(section.id, item.id, value)}
                          >
                            <SelectTrigger className="w-[140px]">
                              <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={STATUS_PHYSICAL}>Physical</SelectItem>
                              <SelectItem value={STATUS_DIGITAL}>Digital</SelectItem>
                              <SelectItem value={STATUS_MISSING}>Missing</SelectItem>
                            </SelectContent>
                          </Select>
                          <label className="cursor-pointer">
                            <Input
                              type="file"
                              accept="image/*,.pdf"
                              className="hidden"
                              onChange={(e) => {
                                handleFileUpload(section.id, item.id, e.target.files?.[0]);
                                e.target.value = '';
                              }}
                            />
                            <span className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                              <Upload className="h-3 w-3" />
                              {upload ? 'Replace' : 'Upload'}
                            </span>
                          </label>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-muted-foreground hover:text-destructive"
                            onClick={() => handleRemoveItem(section.id, item.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        {upload && (
                          <div className="flex items-center gap-2 ml-0 sm:ml-[200px] pl-1">
                            {upload.mimeType?.startsWith('image/') ? (
                              <img
                                src={`data:${upload.mimeType};base64,${upload.data}`}
                                alt={upload.fileName}
                                className="h-10 w-10 object-cover rounded border"
                              />
                            ) : (
                              <FileText className="h-5 w-5 text-muted-foreground" />
                            )}
                            <span className="text-xs text-muted-foreground truncate max-w-[200px]">{upload.fileName}</span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => handleRemoveUpload(section.id, item.id)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <AddCustomItemRow onAdd={(label) => handleAddCustomItem(section.id, label)} />
                </CardContent>
              </Card>
            );
          })}

          <AddCustomSectionRow onAdd={handleAddCustomSection} />
        </div>

        <div className="flex justify-end">
          <Button onClick={handleExport} className="gap-2">
            <Download className="h-4 w-4" />
            Export for binder
          </Button>
        </div>
      </div>
    </ToolLayout>
  );
};

export default DocumentsTool;
